import React, { useEffect, useRef } from 'react';

// Pháo hoa toàn màn hình cho kiểu Đặc biệt: canvas nền trong suốt, không thư viện, không file ngoài.
// Bắn trong suốt thời gian thông báo (trừ khoảng cuối để tàn dần), gỡ khung hình và xoá canvas khi thông báo kết thúc.

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  color: string;
  rocket: boolean;
  targetY: number;
}

const COLORS = ['#FFD54A', '#FF6B6B', '#6BCBFF', '#B388FF', '#7CFFB2', '#FFFFFF', '#FF9F43'];
const GRAVITY = 0.045;
const LAUNCH_EVERY_MS = 380;
// Ngừng bắn trước khi hết giờ để quả cuối kịp nổ và tàn.
const TAIL_MS = 1600;

const rand = (a: number, b: number) => a + Math.random() * (b - a);

export const burst = (x: number, y: number, color: string, count = 60): Particle[] =>
  Array.from({ length: count }, (_, i) => {
    const angle = (Math.PI * 2 * i) / count + rand(-0.05, 0.05);
    const speed = rand(1.6, 4.2);
    return {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 0,
      max: rand(55, 85),
      color,
      rocket: false,
      targetY: 0,
    };
  });

const AlertFireworks: React.FC<{ durationMs: number }> = ({ durationMs }) => {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvas ? canvas.getContext('2d') : null;
    } catch {
      ctx = null;
    }
    if (!canvas || !ctx || typeof window.requestAnimationFrame !== 'function') return undefined;
    const g = ctx;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    let particles: Particle[] = [];
    let raf = 0;
    const startedAt = performance.now();
    let lastLaunch = -LAUNCH_EVERY_MS;

    const launch = () => {
      const w = canvas.width;
      const h = canvas.height;
      particles.push({
        x: rand(w * 0.15, w * 0.85),
        y: h,
        vx: rand(-0.6, 0.6),
        vy: -rand(h / 95, h / 70),
        life: 0,
        max: 200,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rocket: true,
        targetY: rand(h * 0.15, h * 0.45),
      });
    };

    const frame = (now: number) => {
      const elapsed = now - startedAt;
      if (elapsed < durationMs - TAIL_MS && elapsed - lastLaunch >= LAUNCH_EVERY_MS) {
        lastLaunch = elapsed;
        launch();
        if (Math.random() < 0.35) launch();
      }
      // Làm mờ khung trước mà vẫn giữ nền trong suốt (vệt sáng ngắn).
      g.globalCompositeOperation = 'destination-out';
      g.fillStyle = 'rgba(0, 0, 0, 0.25)';
      g.fillRect(0, 0, canvas.width, canvas.height);
      g.globalCompositeOperation = 'lighter';

      const next: Particle[] = [];
      particles.forEach((p) => {
        const q = p;
        q.life += 1;
        if (q.rocket) {
          q.x += q.vx;
          q.y += q.vy;
          q.vy += GRAVITY * 2;
          g.fillStyle = q.color;
          g.fillRect(q.x - 1.5, q.y - 1.5, 3, 6);
          if (q.y <= q.targetY || q.vy >= 0) next.push(...burst(q.x, q.y, q.color));
          else next.push(q);
          return;
        }
        q.vx *= 0.985;
        q.vy = q.vy * 0.985 + GRAVITY;
        q.x += q.vx;
        q.y += q.vy;
        const alpha = Math.max(0, 1 - q.life / q.max);
        if (alpha <= 0) return;
        g.globalAlpha = alpha;
        g.fillStyle = q.color;
        g.beginPath();
        g.arc(q.x, q.y, 2.2, 0, Math.PI * 2);
        g.fill();
        g.globalAlpha = 1;
        next.push(q);
      });
      particles = next;

      if (elapsed < durationMs || particles.length > 0) raf = window.requestAnimationFrame(frame);
      else g.clearRect(0, 0, canvas.width, canvas.height);
    };
    raf = window.requestAnimationFrame(frame);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      particles = [];
      try {
        g.clearRect(0, 0, canvas.width, canvas.height);
      } catch {
        // bỏ qua
      }
    };
  }, [durationMs]);

  return (
    <canvas
      ref={ref}
      data-testid="alert-fireworks"
      aria-hidden="true"
      style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 40 }}
    />
  );
};

export default AlertFireworks;
